import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, ShieldX, Search } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { api } from "@/lib/api";

export default function Verify() {
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get("c") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (value) => {
    const v = (value || "").trim();
    if (!v) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(v)}`);
      setResult(data);
    } catch {
      setError("No certificate found with that number. Check the number and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = params.get("c");
    if (c) lookup(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo title="Verify a Certificate" path="/verify" description="Verify any Planet Trotter Foundation certificate by its certificate number. Every issued certificate carries a unique, publicly checkable number." />
      <PageHero
        eyebrow="Certificates"
        titleLines={["Verify a", "certificate."]}
        intro="Every Planet Trotter certificate carries a unique number. Enter it below, or scan the QR code printed on the certificate."
        image="/dawn/vor-school.jpg"
      />
      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[800px] px-5 sm:px-8">
          <form
            data-testid="verify-form"
            onSubmit={(e) => { e.preventDefault(); lookup(number); }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ptf-text/40" />
              <input
                data-testid="verify-input"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Certificate number, e.g. PTF-2026-123456"
                className="w-full bg-white border border-ptf-primary/20 pl-11 pr-4 py-3.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text placeholder:text-ptf-text/40 uppercase"
              />
            </div>
            <button type="submit" data-testid="verify-submit" disabled={loading} className="bg-ptf-primary text-ptf-bg px-8 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Verify
            </button>
          </form>

          {error && (
            <motion.p data-testid="verify-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
              {error}
            </motion.p>
          )}

          {result && (
            <motion.div data-testid="verify-result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={`mt-8 border p-7 bg-white ${result.status === "VALID" ? "border-emerald-300" : "border-red-300"}`}>
              <div className="flex items-center gap-3">
                {result.status === "VALID"
                  ? <ShieldCheck className="h-7 w-7 text-emerald-600" />
                  : <ShieldX className="h-7 w-7 text-red-600" />}
                <p data-testid="verify-status" className={`font-display uppercase text-2xl tracking-tight ${result.status === "VALID" ? "text-emerald-700" : "text-red-700"}`}>
                  {result.status === "VALID" ? "Valid certificate" : "Revoked certificate"}
                </p>
              </div>
              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                {[["Certificate number", result.cert_number], ["Name", result.name], ["Programme", result.programme], ["Type", result.certificate_type], ["Issue date", result.issue_date], ["Issued by", result.issued_by]].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/45">{k}</dt>
                    <dd className="mt-0.5 text-ptf-primary font-medium">{v || "-"}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>
          )}

          <p className="mt-10 text-xs text-ptf-text/55 leading-relaxed">
            Verification shows only the certificate number, holder name, programme,
            type, issue date and status. No other personal information is exposed.
            Planet Trotter certificates are training and participation certificates
            issued by the Foundation; they are not presented as accredited
            qualifications unless accreditation exists for that programme.
          </p>
        </div>
      </section>
    </>
  );
}
